import { useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useAppSettings } from '../hooks/useAppSettings';
import useApi from './useApi';

/**
 * Hook for fetching user roles from the API.
 * 
 * Pattern follows OrcaWebsite's useGetRole:
 * - getUserRoles() is a simple function that triggers the API call
 * - A useEffect watches the state and processes the response
 * - This prevents infinite loops by not using useCallback with dependencies
 *   that change when roles are set
 */
const useGetUserRoles = () => {
    const { state, getApiData } = useApi();
    const { settings: appSettings } = useAppSettings();
    const { setRoles, setCanEdit, setCanEditChemGuard, setCanAudit } = useContext(AuthContext);

    // Simple function to trigger the API call - no useCallback needed
    // This prevents infinite loops since the function reference doesn't change
    const getUserRoles = () => {
        if (!appSettings?.api) {
            console.warn('API configuration not available yet');
            return;
        }

        const fullUrl = `${appSettings.api}/GetRoles`;
        console.log('🌐 Calling user roles API:', fullUrl);

        getApiData({
            endpoint: fullUrl,
        });
    };

    // Process API response in a separate useEffect (like OrcaWebsite pattern)
    useEffect(() => {
        if (state.status === 'success' && state.result) {
            console.log('✅ GetRoles API response received:', state.result);

            // Parse roles array and set permissions
            if (Array.isArray(state.result)) {
                const rolesArray = state.result as string[];
                setRoles(rolesArray);
                setCanEdit(rolesArray.includes('CommodityBookEditor'));
                setCanEditChemGuard(rolesArray.includes('CommodityBookChemGuardEditor'));
                setCanAudit(rolesArray.includes('CommodityBookAuditor'));
            }
        } else if (state.status === 'error') {
            console.error('GetRoles API error:', state.error);
            // Set fallback permissions on error
            setRoles([]);
            setCanEdit(false);
            setCanEditChemGuard(false);
            setCanAudit(false);
        }
    }, [state, setRoles, setCanEdit, setCanEditChemGuard, setCanAudit]);

    // Parse permissions from current state for immediate access
    const canEdit = state.result && Array.isArray(state.result)
        ? (state.result as string[]).includes('CommodityBookEditor')
        : false;
    const canEditChemGuard = state.result && Array.isArray(state.result)
        ? (state.result as string[]).includes('CommodityBookChemGuardEditor')
        : false;
    const canAudit = state.result && Array.isArray(state.result)
        ? (state.result as string[]).includes('CommodityBookAuditor')
        : false;

    return {
        state,
        canEdit,
        canEditChemGuard,
        canAudit,
        getUserRoles,
    };
};

export default useGetUserRoles;